import React, { useState, useEffect } from "react";
import { Input, List, Spin, Typography, Popover, Button } from "antd";
import { RobotOutlined, SearchOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../configs";

const { Text } = Typography;

/**
 * AI Summary Popover Component
 */
const AISummaryPopover = ({ item }) => {
    const [summary, setSummary] = useState("");
    const [loading, setLoading] = useState(false);

    const fetchSummary = async () => {
        if (summary) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post("/ai/summary", {
                type: item.type,
                id: item.id
            });
            setSummary(res.data?.summary || "Tidak ada ringkasan.");
        } catch (err) {
            console.error(err);
            setSummary("Gagal memuat ringkasan AI.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Popover
            trigger="click"
            title={<span><RobotOutlined style={{ marginRight: 5 }} /> Smart Resume</span>}
            content={
                <div style={{ width: 300, fontSize: 13 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /> Menganalisa...</div>
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: summary.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') }} />
                    )}
                </div>
            }
            onOpenChange={(visible) => {
                if (visible) fetchSummary();
            }}
        >
            <Button
                type="text"
                size="small"
                style={{ color: '#1890ff', fontSize: 12, display: 'flex', alignItems: 'center' }}
                onClick={(e) => e.stopPropagation()}
            >
                <RobotOutlined /> AI
            </Button>
        </Popover>
    );
};

/**
 * Global Search Component for Header
 */
const GlobalSearch = () => {
    const history = useHistory();
    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Debounce Search Effect
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchText && searchText.length >= 2) {
                handleGlobalSearch(searchText);
            } else {
                setSearchResults([]);
            }
        }, 600);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText]);

    const handleGlobalSearch = async (value) => {
        if (!value || value.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        try {
            const res = await axiosInstance.get("/global-search", {
                params: { q: value },
            });
            setSearchResults(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setSearching(false);
        }
    };

    const handleSearchResultClick = (item) => {
        setIsOpen(false);
        setSearchText("");
        setSearchResults([]);

        if (item.type === 'Incoming') {
            history.push(`/inventory/updateincoming/${item.id}`);
        } else if (item.type === 'Outgoing') {
            history.push(`/inventory/updateoutgoing/${item.id}`);
        } else if (item.type === 'Stripping') {
            history.push(`/inventory/updatetallysheet/${item.id}`);
        } else if (item.type === 'Loading') {
            history.push(`/inventory/updatemuat/${item.id}`);
        } else if (item.type === 'Stock') {
            history.push('/monitoring/stock');
        } else if (item.type === 'Adjustment') {
            history.push('/other/adjustment');
        } else if (item.type === 'StockOpname') {
            history.push('/other/opname');
        } else if (item.type === 'PindahLot') {
            history.push('/other/pindah-lot');
        } else if (item.type === 'Pemusnahan') {
            history.push(`/inventory/updatepemusnahan/${item.id}`);
        } else if (item.type === 'Item') {
            history.push('/referensi/komoditas');
        } else if (item.type === 'Company') {
            history.push(`/referensi/kontak/edit/${item.id}`);
        }
    };

    const searchContent = (
        <div style={{ width: 400, maxHeight: 500 }}>
            <Input.Search
                placeholder="Cari Dokumen, Barang, Rak, dll..."
                allowClear
                size="large"
                value={searchText}
                onChange={(e) => {
                    setSearchText(e.target.value);
                    if (!e.target.value) setSearchResults([]);
                }}
                autoFocus
                style={{ marginBottom: 8 }}
            />

            {(searchText && searchText.length >= 2) && (
                <div style={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: searchResults.length > 0 ? "1px solid #f0f0f0" : "none",
                    borderRadius: 4
                }}>
                    {searching ? (
                        <div style={{ textAlign: "center", padding: 20 }}><Spin size="small" /> Mencari...</div>
                    ) : (
                        <List
                            itemLayout="horizontal"
                            dataSource={searchResults}
                            renderItem={item => (
                                <List.Item
                                    style={{ padding: "8px 12px", borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                                    onClick={() => handleSearchResultClick(item)}
                                    actions={[<AISummaryPopover item={item} key="ai" />]}
                                >
                                    <List.Item.Meta
                                        title={<Text strong>[{item.type}] {item.title}</Text>}
                                        description={<Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>}
                                    />
                                </List.Item>
                            )}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: 16, textAlign: "center" }}>
                                        <Text type="secondary">
                                            Tidak ada hasil untuk "{searchText}"
                                        </Text>
                                    </div>
                                )
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );

    return (
        <Popover
            content={searchContent}
            trigger="click"
            open={isOpen}
            onOpenChange={setIsOpen}
            placement="bottomRight"
            overlayStyle={{ padding: 0 }}
        >
            <div
                className="kt-header__topbar-wrapper"
                style={{ cursor: 'pointer' }}
            >
                <span className="kt-header__topbar-icon">
                    <SearchOutlined style={{ fontSize: 20 }} />
                </span>
            </div>
        </Popover>
    );
};

export default GlobalSearch;
